<template>
  <span class="app-expl-wrap">
    <span class="app-expl-wrap-content" :class="{ 'has-expl': hasExpl }" ref="appExplTarget">
      <slot />
    </span>
    <b-popover
      v-if="hasExpl"
      :target="() => $refs.appExplTarget"
      triggers="hover"
      :placement="placement"
      boundary="viewport"
      :custom-class="popoverCustomClass"
      :delay="popoverDelay"
      @show="onPopoverShow"
    >
      <slot name="expl">
        <a v-if="isLink" :href="expl" target="_blank" class="app-expl-wrap-link">
          <span v-html="expl" />
          &nbsp;
          <b-icon icon="box-arrow-up-right" />
        </a>
        <span v-else v-html="expl" />
      </slot>
    </b-popover>
  </span>
</template>

<script lang="ts">
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";

type AppExplWrapPopoverSize = "md" | "lg" | "xl";

/**
 * Hover popovers close immediately. When the explanation is or contains a link (e.g. the
 * severity PI's Knowledge Hub "Learn more") or buttons ("interactive"), keep it open briefly so the
 * pointer can reach it. Showing stays immediate either way.
 */
const POPOVER_DELAY_NONE = 0;
const POPOVER_DELAY_LINK = { show: 0, hide: 400 };

const POPOVER_SIZE_CLASS: Record<AppExplWrapPopoverSize, string> = {
  md: "popover-md",
  lg: "popover-lg",
  xl: "popover-xl",
};

@Component({
  name: "app-expl-wrap",
})
export default class AppExplWrap extends Vue {
  @Prop({ default: "" }) expl!: string;
  @Prop({ default: "top" }) placement!: string;
  @Prop({ default: false }) isLink!: string;
  @Prop({ default: "md" }) popoverSize!: AppExplWrapPopoverSize;
  // The popover holds buttons or links the pointer has to reach
  @Prop({ default: false }) interactive!: boolean;

  get hasExpl(): boolean {
    return !!(this.expl || this.$slots.expl);
  }

  get popoverDelay(): number | { show: number; hide: number } {
    return this.needsLinkHideDelay ? POPOVER_DELAY_LINK : POPOVER_DELAY_NONE;
  }

  get needsLinkHideDelay(): boolean {
    return !!(this.interactive || this.isLink || (this.expl && /<a\b/i.test(this.expl)));
  }

  get popoverCustomClass(): string {
    return POPOVER_SIZE_CLASS[this.popoverSize];
  }

  // Lets content that is expensive to get load when it is about to be shown
  onPopoverShow() {
    this.$emit("show");
  }
}
</script>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-expl-wrap {
  &-content.has-expl {
    text-decoration: underline;
    text-decoration-color: var(--primary-bg-color);;
    text-decoration-style: dotted;
    text-decoration-thickness: 1px;
  }
  &-link {
    display: block;
  }
}

</style>
